/**
 * Quotations for cryptograms: public domain, with the work or occasion they come from.
 *
 * Every quotation is by an author whose work is out of copyright (the author died before
 * 1955 and the work appeared before 1928), or is an anonymous proverb, a speech or an
 * official document. Each names its source, so a teacher can check the wording. Spelling
 * is modernised only where noted. Lines that are often quoted wrongly — "Be the change",
 * "Be yourself; everyone else is already taken", "Genius is one percent inspiration" and
 * the like — are deliberately left out, because no source for them could be given.
 */

export type QuoteCategory =
  | "shakespeare"
  | "literature"
  | "speeches"
  | "proverbs";

export const QUOTE_CATEGORIES: readonly QuoteCategory[] = [
  "shakespeare",
  "literature",
  "speeches",
  "proverbs",
];

export interface Quote {
  id: string;
  text: string;
  /** "Proverb" for the anonymous ones. */
  author: string;
  /** The work, speech or collection. */
  source: string;
  /** Year of the work; absent for proverbs. */
  published?: number;
  /** Year the author died; absent for proverbs. */
  died?: number;
  category: QuoteCategory;
}

export const QUOTES: readonly Quote[] = [
  {
    id: "shakespeare-stage",
    text: "All the world's a stage, and all the men and women merely players.",
    author: "William Shakespeare",
    source: "As You Like It, Act 2, Scene 7",
    published: 1623,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-self",
    text: "This above all: to thine own self be true.",
    author: "William Shakespeare",
    source: "Hamlet, Act 1, Scene 3",
    published: 1603,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-love",
    text: "The course of true love never did run smooth.",
    author: "William Shakespeare",
    source: "A Midsummer Night's Dream, Act 1, Scene 1",
    published: 1600,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-brevity",
    text: "Brevity is the soul of wit.",
    author: "William Shakespeare",
    source: "Hamlet, Act 2, Scene 2",
    published: 1603,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-thinking",
    text: "There is nothing either good or bad, but thinking makes it so.",
    author: "William Shakespeare",
    source: "Hamlet, Act 2, Scene 2",
    published: 1603,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-early",
    text: "Better three hours too soon than a minute too late.",
    author: "William Shakespeare",
    source: "The Merry Wives of Windsor, Act 2, Scene 2",
    published: 1602,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-great",
    text: "Some are born great, some achieve greatness, and some have greatness thrust upon them.",
    author: "William Shakespeare",
    source: "Twelfth Night, Act 2, Scene 5",
    published: 1623,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-may-be",
    text: "We know what we are, but know not what we may be.",
    author: "William Shakespeare",
    source: "Hamlet, Act 4, Scene 5",
    published: 1603,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-valour",
    text: "The better part of valour is discretion.",
    author: "William Shakespeare",
    source: "Henry IV, Part 1, Act 5, Scene 4",
    published: 1598,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "shakespeare-gold",
    text: "All that glisters is not gold.",
    author: "William Shakespeare",
    source: "The Merchant of Venice, Act 2, Scene 7",
    published: 1600,
    died: 1616,
    category: "shakespeare",
  },
  {
    id: "austen-truth",
    text: "It is a truth universally acknowledged, that a single man in possession of a good fortune, must be in want of a wife.",
    author: "Jane Austen",
    source: "Pride and Prejudice, 1813",
    published: 1813,
    died: 1817,
    category: "literature",
  },
  {
    id: "austen-reading",
    text: "I declare after all there is no enjoyment like reading!",
    author: "Jane Austen",
    source: "Pride and Prejudice, 1813, chapter 11",
    published: 1813,
    died: 1817,
    category: "literature",
  },
  {
    id: "dickens-times",
    text: "It was the best of times, it was the worst of times.",
    author: "Charles Dickens",
    source: "A Tale of Two Cities, 1859",
    published: 1859,
    died: 1870,
    category: "literature",
  },
  {
    id: "dickens-more",
    text: "Please, sir, I want some more.",
    author: "Charles Dickens",
    source: "Oliver Twist, 1838",
    published: 1838,
    died: 1870,
    category: "literature",
  },
  {
    id: "carroll-curiouser",
    text: "Curiouser and curiouser!",
    author: "Lewis Carroll",
    source: "Alice's Adventures in Wonderland, 1865, chapter 2",
    published: 1865,
    died: 1898,
    category: "literature",
  },
  {
    id: "carroll-begin",
    text: "Begin at the beginning, and go on till you come to the end: then stop.",
    author: "Lewis Carroll",
    source: "Alice's Adventures in Wonderland, 1865, chapter 12",
    published: 1865,
    died: 1898,
    category: "literature",
  },
  {
    id: "carroll-impossible",
    text: "Why, sometimes I've believed as many as six impossible things before breakfast.",
    author: "Lewis Carroll",
    source: "Through the Looking-Glass, 1871, chapter 5",
    published: 1871,
    died: 1898,
    category: "literature",
  },
  {
    id: "twain-training",
    text: "Training is everything. The peach was once a bitter almond; cauliflower is nothing but cabbage with a college education.",
    author: "Mark Twain",
    source: "Pudd'nhead Wilson, 1894",
    published: 1894,
    died: 1910,
    category: "literature",
  },
  {
    id: "twain-courage",
    text: "Courage is resistance to fear, mastery of fear, not absence of fear.",
    author: "Mark Twain",
    source: "Pudd'nhead Wilson, 1894",
    published: 1894,
    died: 1910,
    category: "literature",
  },
  {
    id: "wilde-temptation",
    text: "I can resist everything except temptation.",
    author: "Oscar Wilde",
    source: "Lady Windermere's Fan, 1892",
    published: 1892,
    died: 1900,
    category: "literature",
  },
  {
    id: "wilde-experience",
    text: "Experience is merely the name men gave to their mistakes.",
    author: "Oscar Wilde",
    source: "The Picture of Dorian Gray, 1891",
    published: 1891,
    died: 1900,
    category: "literature",
  },
  {
    id: "thoreau-desperation",
    text: "The mass of men lead lives of quiet desperation.",
    author: "Henry David Thoreau",
    source: "Walden, 1854",
    published: 1854,
    died: 1862,
    category: "literature",
  },
  {
    id: "emerson-misunderstood",
    text: "To be great is to be misunderstood.",
    author: "Ralph Waldo Emerson",
    source: "Self-Reliance, 1841",
    published: 1841,
    died: 1882,
    category: "literature",
  },
  {
    id: "emerson-enthusiasm",
    text: "Nothing great was ever achieved without enthusiasm.",
    author: "Ralph Waldo Emerson",
    source: "Circles, 1841",
    published: 1841,
    died: 1882,
    category: "literature",
  },
  {
    id: "dickinson-hope",
    text: "Hope is the thing with feathers that perches in the soul, and sings the tune without the words, and never stops at all.",
    author: "Emily Dickinson",
    source: "Poems, 1891 (written about 1861)",
    published: 1891,
    died: 1886,
    category: "literature",
  },
  {
    id: "whitman-multitudes",
    text: "I am large, I contain multitudes.",
    author: "Walt Whitman",
    source: "Song of Myself, 1855",
    published: 1855,
    died: 1892,
    category: "literature",
  },
  {
    id: "pope-err",
    text: "To err is human, to forgive, divine.",
    author: "Alexander Pope",
    source: "An Essay on Criticism, 1711",
    published: 1711,
    died: 1744,
    category: "literature",
  },
  {
    id: "bulwer-pen",
    text: "The pen is mightier than the sword.",
    author: "Edward Bulwer-Lytton",
    source: "Richelieu; Or, The Conspiracy, 1839",
    published: 1839,
    died: 1873,
    category: "literature",
  },
  {
    id: "newton-giants",
    text: "If I have seen further it is by standing on the shoulders of giants.",
    author: "Isaac Newton",
    source: "Letter to Robert Hooke, 5 February 1676",
    published: 1676,
    died: 1727,
    category: "literature",
  },
  {
    id: "darwin-grandeur",
    text: "There is grandeur in this view of life.",
    author: "Charles Darwin",
    source: "On the Origin of Species, 1859",
    published: 1859,
    died: 1882,
    category: "literature",
  },
  {
    id: "lincoln-house",
    text: "A house divided against itself cannot stand.",
    author: "Abraham Lincoln",
    source: "Speech at Springfield, Illinois, 16 June 1858",
    published: 1858,
    died: 1865,
    category: "speeches",
  },
  {
    id: "lincoln-malice",
    text: "With malice toward none, with charity for all.",
    author: "Abraham Lincoln",
    source: "Second Inaugural Address, 4 March 1865",
    published: 1865,
    died: 1865,
    category: "speeches",
  },
  {
    id: "lincoln-people",
    text: "Government of the people, by the people, for the people, shall not perish from the earth.",
    author: "Abraham Lincoln",
    source: "Gettysburg Address, 19 November 1863",
    published: 1863,
    died: 1865,
    category: "speeches",
  },
  {
    id: "jefferson-equal",
    text: "We hold these truths to be self-evident, that all men are created equal.",
    author: "Thomas Jefferson",
    source: "Declaration of Independence, 1776",
    published: 1776,
    died: 1826,
    category: "speeches",
  },
  {
    id: "franklin-done",
    text: "Well done is better than well said.",
    author: "Benjamin Franklin",
    source: "Poor Richard's Almanack, 1737",
    published: 1737,
    died: 1790,
    category: "speeches",
  },
  {
    id: "franklin-taxes",
    text: "In this world nothing can be said to be certain, except death and taxes.",
    author: "Benjamin Franklin",
    source: "Letter to Jean-Baptiste Le Roy, 13 November 1789",
    published: 1789,
    died: 1790,
    category: "speeches",
  },
  {
    id: "proverb-stitch",
    text: "A stitch in time saves nine.",
    author: "Proverb",
    source: "Recorded by Thomas Fuller, Gnomologia, 1732",
    category: "proverbs",
  },
  {
    id: "proverb-actions",
    text: "Actions speak louder than words.",
    author: "Proverb",
    source: "English proverb",
    category: "proverbs",
  },
  {
    id: "proverb-journey",
    text: "A journey of a thousand miles begins with a single step.",
    author: "Laozi",
    source: "Tao Te Ching, chapter 64 (translations vary)",
    category: "proverbs",
  },
  {
    id: "proverb-cloud",
    text: "Every cloud has a silver lining.",
    author: "Proverb",
    source: "English proverb",
    category: "proverbs",
  },
  {
    id: "proverb-smoke",
    text: "Where there's smoke, there's fire.",
    author: "Proverb",
    source: "English proverb",
    category: "proverbs",
  },
  {
    id: "proverb-bird",
    text: "The early bird catches the worm.",
    author: "Proverb",
    source: "Recorded by John Ray, A Collection of English Proverbs, 1670",
    category: "proverbs",
  },
];

export function quotesIn(category: QuoteCategory | "all"): readonly Quote[] {
  return category === "all"
    ? QUOTES
    : QUOTES.filter((quote) => quote.category === category);
}

export function findQuote(id: string): Quote | undefined {
  return QUOTES.find((quote) => quote.id === id);
}
